/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';
import {useSignals} from '@preact/signals-react/runtime';
import {liveJobsEnabled} from './signals/liveUpdates';
import {
  getJobIds,
  getWatchCreatedJobsDefault,
  isWatched,
  setWatchCreatedJobsDefault,
  subscribe,
  toggleWatch,
  version
} from './WatchedJobs';

export interface WatchedJobResult {
  watched: boolean,
  toggle: () => void
}

export interface WatchedJobIdsResult {
  jobIds: number[],
  revision: number
}

export interface WatchCreatedJobsDefaultResult {
  show: boolean,
  enabled: boolean,
  setEnabled: (on: boolean) => void
}

/**
 * Repaints the caller after every mutation of the watch list, and hands back
 * the module's version counter for anything which needs a value to depend on.
 */
const useWatchedJobsRevision = (): number => {
  const [revision, setRevision] = useState<number>(version);

  useEffect(() => subscribe(function() {
    setRevision(version());
  }), []);

  return revision;
};

/**
 * Whether one job is watched, repainting the caller only when *that* job's flag
 * changes.
 *
 * The list is a single module singleton with a single listener registry, so
 * every mounted bell hears every mutation - and the grid renders a page of
 * them, twenty at the default page size. Holding the flag rather than the
 * module's revision counter is what keeps one toggle to one repaint: React
 * bails out of re-rendering when the value handed to a setter is the one
 * already in state, so the other nineteen run the setter and stop there.
 * `React.memo` cannot do this, since the re-render originates inside each
 * instance's own state rather than in its props.
 *
 * The flag is re-read when `jobId` changes as well as on notification, so a
 * remounted cell handed a different job never renders the previous one's value.
 */
const useWatchedFlag = (jobId: number): boolean => {
  const [watched, setWatched] = useState<boolean>(() => isWatched(jobId));

  useEffect(() => {
    setWatched(isWatched(jobId));

    return subscribe(function() {
      setWatched(isWatched(jobId));
    });
  }, [jobId]);

  return watched;
};

/**
 * Reads and toggles the watch on one job. The returned `watched` flag is the
 * in-memory value, so it flips on click rather than on the save which follows.
 *
 * ```typescript
 *
 *   import {useWatchedJob} from '../useWatchedJob';
 *
 *   const {watched, toggle} = useWatchedJob(jobId);
 *
 * ```
 *
 * @param   jobId The job the caller is rendering a bell for.
 * @returns Whether the job is watched, and a toggle which persists the change.
 */
export const useWatchedJob = (jobId: number): WatchedJobResult => {
  const watched = useWatchedFlag(jobId);

  const toggle = useCallback((): void => {
    toggleWatch(jobId);
  }, [jobId]);

  return {watched, toggle};
};

/**
 * The whole watch list, for the surfaces which render it rather than one job.
 *
 * `revision` is what a `useMemo` depends on: the id array is the module's own,
 * and a memo cannot observe a plain singleton changing underneath it.
 *
 * ```typescript
 *
 *   import {useWatchedJobIds} from './useWatchedJob';
 *
 *   const {jobIds, revision} = useWatchedJobIds();
 *   const query = useMemo(() => buildWatchedJobsQuery(jobIds), [revision]);
 *
 * ```
 *
 * @returns The watched ids in insertion order, and the module's version.
 */
export const useWatchedJobIds = (): WatchedJobIdsResult => {
  const revision = useWatchedJobsRevision();

  return {jobIds: getJobIds(), revision};
};

/**
 * The create-job notification default, for the My Profile preferences row.
 *
 * The row itself is DLS, which cannot see either this module or ADM's feature
 * flag, so it takes the three values below as props. Keeping the read and the
 * write here is what leaves `WatchedJobs` the blob's only writer.
 *
 * The write happens on change rather than on the profile dialog's Save: the
 * value does not live at a flat `Preference` key, so it cannot join the batch
 * that Save flushes, and storing the same intent twice to make it fit is worse
 * than a row which saves like a settings-page switch.
 *
 * ```typescript
 *
 *   import {useWatchCreatedJobsDefault} from '.../useWatchedJob';
 *
 *   const {show, enabled, setEnabled} = useWatchCreatedJobsDefault();
 *
 * ```
 *
 * @returns Whether to show the row, its current value, and its setter.
 */
export const useWatchCreatedJobsDefault = (): WatchCreatedJobsDefaultResult => {
  useSignals();
  useWatchedJobsRevision();

  const setEnabled = useCallback((on: boolean): void => {
    setWatchCreatedJobsDefault(on);
  }, []);

  return {
    show    : liveJobsEnabled.value,
    enabled : getWatchCreatedJobsDefault(),
    setEnabled
  };
};

export default useWatchedJob;
